/**
 * Machine — scrollytelling.
 *
 * Progression dans la section : p = -haut / (hauteur de section − hauteur de fenêtre).
 * De p = 0 à p = 0,2 la machine passe de scale .94 à 1 et le halo s'allume ;
 * chaque point apparaît dès que p dépasse 0,04 + son rang × 0,1 ; et la machine
 * fait un demi-tour pendant toute la durée d'apparition des points.
 *
 * La rotation de la machine est une séquence d'images détourées, échangées
 * au fil du défilement : une image par position. Rien n'est décodé au
 * passage, contrairement au repositionnement d'une vidéo, d'où la fluidité.
 *
 * Les images sont préchargées et décodées avant toute bascule : sans cela la
 * première apparition de chacune provoquerait un clignotement.
 *
 * Les lectures de position se font dans un rAF : l'écouteur de scroll ne
 * déclenche jamais de calcul de mise en page synchrone.
 */
(() => {
  const section = document.querySelector('[data-oyw-machine]');
  if (!section) return;

  const photo = section.querySelector('[data-oyw-photo]');
  const halo = section.querySelector('[data-oyw-halo]');
  const points = [...section.querySelectorAll('[data-oyw-point]')];

  const vue = section.querySelector('[data-oyw-vue]');
  const liste = section.querySelector('[data-oyw-sequence]');

  /** Adresses des images du tour complet, dans l'ordre. */
  let sequence = [];
  if (liste) {
    try {
      sequence = JSON.parse(liste.textContent);
    } catch (e) {
      sequence = [];
    }
  }

  /** Images déjà décodées, par rang. */
  const pretes = new Array(sequence.length).fill(false);
  let rangAffiche = 0;

  /** Part du défilement sur laquelle la rotation se déroule, calée sur les
      points : le dernier apparaît vers p = 0,54. */
  const COURSE_PIVOT = 0.6;

  const sansMouvement = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Sous 900px la section n'est plus sticky : la mise en page empilée montre tout.
  const surBureau = window.matchMedia('(min-width: 901px)');

  /** Tout afficher, sans animation : préférence système ou affichage mobile. */
  const toutAfficher = () => {
    points.forEach((point) => point.setAttribute('data-visible', ''));
    // L'état figé doit être l'état d'arrivée, pas l'état de départ.
    if (photo) photo.style.transform = 'scale(1)';

    // Sans défilement pour la piloter, la machine s'arrête sur une pose de
    // trois quarts, la plus parlante des images de la séquence.
    if (vue && sequence.length > 0) {
      const rang = rangDisponible(Math.round((sequence.length - 1) * 0.5));
      if (rang >= 0 && rang !== rangAffiche) {
        rangAffiche = rang;
        vue.src = sequence[rang];
      }
    }
    if (halo) halo.style.opacity = '';
  };

  /**
   * Précharge et décode la séquence.
   *
   * Chaque image devient utilisable dès qu'elle est décodée, sans attendre les
   * autres : attendre l'ensemble laissait la machine immobile pendant tout le
   * premier défilement, le temps du chargement.
   */
  const prechargerSequence = () => {
    sequence.forEach((adresse, rang) => {
      const img = new Image();
      img.src = adresse;
      const marquer = () => {
        pretes[rang] = true;
        dessiner();
      };
      // decode() manque à quelques navigateurs anciens : on se rabat sur onload.
      if (img.decode) img.decode().then(marquer).catch(marquer);
      else img.onload = marquer;
    });
  };

  /**
   * Le rang décodé le plus proche d'un rang visé.
   *
   * Tant que la séquence charge, on montre l'image disponible la plus proche
   * plutôt que rien : la rotation démarre aussitôt, et s'affine à mesure.
   */
  const rangDisponible = (vise) => {
    if (pretes[vise]) return vise;
    for (let ecart = 1; ecart < sequence.length; ecart += 1) {
      if (pretes[vise - ecart]) return vise - ecart;
      if (pretes[vise + ecart]) return vise + ecart;
    }
    return -1;
  };

  /**
   * Affiche l'image correspondant à la progression.
   *
   * L'image est choisie par son rang, et on ne touche au src que si le rang a
   * changé : réaffecter la même adresse relancerait un cycle de rendu pour rien.
   */
  const afficherVue = (p) => {
    if (!vue || sequence.length === 0) return;

    const q = Math.min(1, Math.max(0, p / COURSE_PIVOT));
    const vise = Math.min(sequence.length - 1, Math.round(q * (sequence.length - 1)));
    const rang = rangDisponible(vise);

    // On ne touche au src que si le rang change : réaffecter la même adresse
    // relancerait un cycle de rendu pour rien.
    if (rang >= 0 && rang !== rangAffiche) {
      rangAffiche = rang;
      vue.src = sequence[rang];
    }
  };

  /** Mise à l'échelle du conteneur : elle se joue sur le premier cinquième. */
  const echelleMachine = (p) => `scale(${(0.94 + 0.06 * Math.min(1, p / 0.2)).toFixed(4)})`;

  let enAttente = false;

  const dessiner = () => {
    enAttente = false;

    const rect = section.getBoundingClientRect();
    const course = rect.height - window.innerHeight;
    const p = Math.min(1, Math.max(0, -rect.top / Math.max(1, course)));

    const k = Math.min(1, p / 0.2);
    if (photo) photo.style.transform = echelleMachine(p);
    afficherVue(p);
    // Le halo s'allume sur le premier cinquième, comme la mise à l'échelle.
    if (halo) {
      halo.style.opacity = String(k);
      halo.style.transform = `translate(-50%, -50%) scale(${0.6 + 0.4 * k})`;
    }

    points.forEach((point) => {
      const ordre = Number(point.dataset.ordre) || 0;
      if (p > 0.04 + ordre * 0.1) {
        point.setAttribute('data-visible', '');
      } else {
        point.removeAttribute('data-visible');
      }
    });
  };

  const auScroll = () => {
    if (enAttente) return;
    enAttente = true;
    window.requestAnimationFrame(dessiner);
  };

  const OPTIONS = { passive: true, capture: true };

  const brancher = () => {
    const actif = surBureau.matches && !sansMouvement.matches;
    window.removeEventListener('scroll', auScroll, OPTIONS);

    if (!actif) {
      toutAfficher();
      return;
    }

    window.addEventListener('scroll', auScroll, OPTIONS);
    dessiner();
  };

  prechargerSequence();
  brancher();
  surBureau.addEventListener('change', brancher);
  sansMouvement.addEventListener('change', brancher);
})();
